import { Icons } from "assets";
import classNames from "classnames";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomButton from "shared/components/common/customButton";
import CustomPagination from "shared/components/common/customPagination";
import CustomRadioButton from "shared/components/common/customRadioButton";
import CustomSelectTable from "shared/components/common/customSelectTable";
import CustomToolTip from "shared/components/common/customToolTip";
import { toastMessage } from "shared/components/common/toast";
import useDebounce from "shared/hooks/useDebounce";
import SekeletonLoader from "shared/loaders/skeleton";
import { setArchiveCountReducer } from "shared/redux/reducers/archiveCount";
import { routeConstant } from "shared/routes/routeConstant";
import {
  DownloadEvaluationsService,
  GetArchiveCountService,
  GetArchivedEvaluationsService,
  UnArchiveEvaluationsService,
} from "shared/services/evaluations";
import styles from "./styles.module.scss";
import { exportBulkToExcel } from "shared/services/exportBulkExcel";
import { setEvaluationsReducer } from "shared/redux/reducers/evaluations";

const ArchivedEvaluations = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allEvaluationsRef = useRef<any[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const [archiveLoading, setArchiveLoading] = useState<boolean>(false);
  const [downlaodLoading, setDownlaodLoading] = useState<boolean>(false);
  const [evalIds, setEvalIds] = useState<string[]>([]);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [search, setSearch] = useState<string>("");
  const [searchVal, setSearchVal] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>("");
  const [order, setOrder] = useState<string>("");

  const handleGetCount = () => {
    GetArchiveCountService()
      .then(({ data }) => {
        dispatch(setArchiveCountReducer({ count: data }));
      })
      .catch((err) => {});
  };

  const handleGetEvaluations = (page: number) => {
    setLoading(true);
    GetArchivedEvaluationsService({
      search: searchVal,
      pageNo: page,
      sortBy,
      order,
    })
      .then(({ data: { data, total } }) => {
        setEvaluations(data);
        setTotal(total);
        allEvaluationsRef.current = [...allEvaluationsRef.current, ...data];
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const handleMultipleEvaluation = () => {
    let selectedEvals = allEvaluationsRef.current.filter((evaluation) =>
      evalIds.includes(evaluation.id)
    );
    dispatch(setEvaluationsReducer({ evaluations: selectedEvals }));
    navigate(routeConstant.multipleEvaluationRecordDetail.path);
  };

  const handleUnArchiveEvaluation = () => {
    setArchiveLoading(true);
    UnArchiveEvaluationsService({ evalIds: evalIds })
      .then(() => {
        toastMessage("success", "Evaluation unarchived successfully");
        let tempEvals = evaluations.filter(
          (item) => !evalIds.includes(item.id)
        );
        setEvaluations(tempEvals);
        setEvalIds([]);
        handleGetCount();
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setArchiveLoading(false);
      });
  };

  const handleDownlaodEvals = () => {
    setDownlaodLoading(true);
    DownloadEvaluationsService({
      evalIds: evalIds,
    })
      .then(({ data, status }) => {
        if (status) {
          exportBulkToExcel(data);
        }
      })
      .catch((err) => {
        toastMessage("error", err?.response?.data?.error);
      })
      .finally(() => {
        setDownlaodLoading(false);
      });
  };

  useEffect(() => {
    setPage(1);
    setEvaluations([]);
    setLoading(true);
    handleGetEvaluations(1);
    // eslint-disable-next-line
  }, [searchVal, sortBy, order]);

  useDebounce(
    () => {
      setSearchVal(search);
    },
    [search],
    800
  );

  return (
    <div className={classNames(styles.mainContainer)}>
      <div
        className={classNames(
          "flex items-start gap-3  justify-between flex-col lg:flex-row px-4 py-4",
          styles.tableMainHead
        )}
      >
        <div
          className={classNames(
            "flex w-full lg:w-2/5  2xl:w-1/5 flex-col items-start"
          )}
        >
          <h1 className={classNames(styles.tableMainHeading)}>
            Evaluation Record History
          </h1>
          {!loading ? (
            <p className={classNames(styles.tableMainTitle)}>
              Showing results {(page - 1) * 10 + 1} -{" "}
              {(page - 1) * 10 + evaluations?.length} of {total}
            </p>
          ) : (
            <p className={classNames(styles.tableMainTitle)}>
              Showing all Evaluations
            </p>
          )}
        </div>
        <div
          className={classNames(
            "flex w-full lg:w-5/6 flex-wrap items-center gap-3 justify-end"
          )}
        >
          {evalIds?.length > 0 ? (
            <>
              <label className={classNames(styles.evalCountLabel)}>
                {String(evalIds?.length).padStart(2, "0")} Evaluation
                {evalIds?.length > 1 ? "s " : " "}
                Selected
              </label>
              <CustomButton
                Icon={Icons.Archive}
                IconDirction="left"
                title="Unarchive"
                containerStyle={classNames(styles.buttonStyle)}
                onClick={handleUnArchiveEvaluation}
                loading={archiveLoading}
                disabled={archiveLoading}
              />
              <CustomButton
                Icon={Icons.Eye}
                IconDirction="left"
                title="View"
                containerStyle={classNames(styles.buttonStyle)}
                onClick={handleMultipleEvaluation}
              />
              <CustomButton
                Icon={Icons.Downlaod}
                IconDirction="left"
                title="Download"
                loading={downlaodLoading}
                disabled={downlaodLoading}
                containerStyle={classNames(styles.buttonStyle)}
                onClick={handleDownlaodEvals}
              />
            </>
          ) : null}

          <div className={classNames(styles.searchContainer, "flex")}>
            <Icons.Search className={classNames(styles.searchIconStyle)} />
            <input
              className={classNames(styles.searchInputStyle, "ps-2")}
              value={search}
              placeholder="Search Job Title / Dept"
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>
        </div>
      </div>
      <CustomSelectTable
        evaluations={evaluations}
        evalIds={evalIds}
        title={"Evaluation Record History"}
        isEmpty={evaluations.length === 0}
        loading={loading}
        setEvalIds={setEvalIds}
        setSortBy={setSortBy}
        setOrder={setOrder}
        sortBy={sortBy}
        order={order}
      >
        {loading ? (
          <>
            {Array.from(Array(5).keys()).map((_, index) => {
              return <TableRowLoader key={index} />;
            })}
          </>
        ) : (
          <>
            {evaluations.map((evaluation, index) => {
              return (
                <TableRow
                  item={evaluation}
                  key={evaluation?.id}
                  index={index}
                  evalIds={evalIds}
                  setEvalIds={setEvalIds}
                />
              );
            })}
          </>
        )}
      </CustomSelectTable>

      <div
        className={classNames(
          "w-full px-4 py-4",
          total >= 10 && styles.border,
          styles.paginationContainer
        )}
      >
        <CustomPagination
          currentPage={page}
          totalCount={total}
          pageSize={10}
          onPageChange={(page: any) => {
            setPage(page);
            handleGetEvaluations(page);
          }}
        />
      </div>
    </div>
  );
};

const TableRow = ({ item, evalIds, setEvalIds }: any) => {
  const navigate = useNavigate();

  return (
    <>
      <tr>
        <td
          className={classNames(
            styles.tableContentLabel,
            "ps-2 pe-2 py-3 sticky-col check-box-first-col"
          )}
          style={{ verticalAlign: "middle" }}
        >
          <div className={classNames("flex items-center justify-center")}>
            <CustomRadioButton
              active={evalIds.includes(item?.id)}
              className={classNames(styles.customRadioStyle2)}
              onClick={() => {
                if (evalIds.includes(item?.id)) {
                  setEvalIds(evalIds.filter((id: any) => id !== item?.id));
                } else {
                  setEvalIds([...evalIds, item?.id]);
                }
              }}
            />
          </div>
        </td>

        <td
          className={classNames(
            styles.tableContentLabel,
            "px-2 py-3 sticky-col check-box-second-col"
          )}
        >
          {item?.current_version?.jobTitle}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "px-2 py-3")}
          style={{ maxWidth: "200px" }}
        >
          {item?.current_version?.department}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "px-2 py-3")}
          style={{ maxWidth: "100px" }}
        >
          {item?.jobCode}
        </td>

        <td
          className={classNames(
            styles.tableContentLabel,
            styles.highlighted,
            "px-2 py-3"
          )}
        >
          {item?.current_version?.rasScore}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.fromAdmin
            ? `CGP (${item?.evaluated_by?.name})`
            : item?.evaluated_by?.name}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {moment(item?.current_version?.created_at).format("MMM DD, YYYY")}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-4 py-3")}
          style={{ verticalAlign: "middle", textAlign: "center" }}
        >
          <div className={classNames("flex items-center gap-2 justify-center")}>
            <CustomToolTip label="See Details">
              <div
                className={classNames(styles.actionContainer)}
                onClick={() => {
                  navigate(
                    routeConstant.evaluationRecordDetail.path.replace(
                      ":id",
                      item?.id
                    )
                  );
                }}
              >
                <Icons.Eye />
              </div>
            </CustomToolTip>
          </div>
        </td>
      </tr>
    </>
  );
};
const TableRowLoader = () => {
  return (
    <>
      <tr>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-2 py-3")}
          style={{ verticalAlign: "middle" }}
        >
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>

        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-2 py-3")}
          style={{ verticalAlign: "middle" }}
        >
          <div className={classNames("flex items-center justify-center gap-2")}>
            <SekeletonLoader
              className={classNames(styles.actionContainerLoader)}
            />
            <SekeletonLoader
              className={classNames(styles.actionContainerLoader)}
            />
          </div>
        </td>
      </tr>
    </>
  );
};

export default ArchivedEvaluations;
